import { Tooltip, TooltipContent, TooltipTrigger } from 'components';
import {
  INFLUENZA_ALERT_KEY,
  type AlertSegment,
  type PreventionAlert,
} from 'types/preventionAlerts';
import {
  AlertIcon,
  AlertSymbol,
  AlertYear,
  CompositeSegment,
  IconLabel,
  ItemContainer,
  LabelStack,
  SegmentGroup,
} from './PreventionAlertsBar.styles';
import { t } from 'i18next';
const getSegmentPosition = (
  kind: PreventionAlert['display']['kind'],
  index: number
): 'left' | 'right' | undefined => {
  if (kind !== 'composite') {
    return undefined;
  }
  return index === 0 ? 'left' : 'right';
};

const renderSegmentContent = (seg: AlertSegment, alert: PreventionAlert): React.ReactNode => {
  const fixedIconPath = alert.gopCode;
  if (seg.icon) {
    return (
      <AlertIcon
        src={`/images/prevention-alerts/${seg.icon}.svg`}
        alt={seg.key}
        $fillsBox={seg.icon === INFLUENZA_ALERT_KEY}
      />
    );
  }

  if (seg.label) {
    return (
      <LabelStack>
        <IconLabel>{seg.label}</IconLabel>
        {alert.mostRecentYear && (
          <AlertYear>{String(alert.mostRecentYear % 100).padStart(2, '0')}</AlertYear>
        )}
      </LabelStack>
    );
  }

  if (fixedIconPath) {
    return <AlertIcon src={fixedIconPath} alt={alert.key} />;
  }

  return null;
};

const Segment: React.FC<{
  seg: AlertSegment;
  alert: PreventionAlert;
  position?: 'left' | 'right';
  stacked: boolean;
}> = ({ seg, alert, position, stacked }) => {
  const content = renderSegmentContent(seg, alert);

  if (position) {
    return (
      <CompositeSegment $status={seg.status} $position={position} $stacked={stacked}>
        {content}
      </CompositeSegment>
    );
  }

  return <AlertSymbol $eligibleNow={seg.status === 'pending'}>{content}</AlertSymbol>;
};

interface AlertItemProps {
  alert: PreventionAlert;
  stacked?: boolean;
}

export const AlertItem: React.FC<AlertItemProps> = ({ alert, stacked = false }) => (
  <ItemContainer>
    <Tooltip>
      <TooltipTrigger asChild>
        <SegmentGroup $stacked={stacked}>
          {alert.display.segments.map((seg, index) => (
            <Segment
              key={seg.key}
              seg={seg}
              alert={alert}
              position={getSegmentPosition(alert.display.kind, index)}
              stacked={stacked}
            />
          ))}
        </SegmentGroup>
      </TooltipTrigger>
      <TooltipContent>
        <p>{t(`result.preventions.GOP_alerts.${alert.alertText}`)}</p>
      </TooltipContent>
    </Tooltip>
  </ItemContainer>
);
